/**
 * On-demand BPM / offset detection for the Flow editor's CONTROLS panel.
 *
 *  • detectAudioOffset() — fully client-side. Decodes the currently loaded
 *    track and scans for the first audible onset, skipping the leading
 *    silence / low-level white noise at the start of the song. Returns the
 *    onset time in seconds (what the OFFSET field expects).
 *
 *  • detectAudioBpm() — re-runs the backend librosa analysis on the cached
 *    bytes (same endpoint the loader uses) and returns the detected BPM.
 *
 * Both reuse whatever audio is already loaded — no re-pick needed.
 */

import { useFlowStore } from './flowStore';
import { readCachedAudioBytes } from './audioCache';
import { API_BASE } from '../../api/apiBase';

/** Grab the current track as raw bytes — cached copy first, live blob URL as
 *  a fallback. Throws when no audio is loaded. */
async function getCurrentAudioBytes(): Promise<{ bytes: ArrayBuffer; name: string; type: string }> {
  const cached = await readCachedAudioBytes();
  if (cached) return cached;

  const { audioUrl, audioName } = useFlowStore.getState();
  if (!audioUrl) throw new Error('No audio loaded');
  const res = await fetch(audioUrl);
  const bytes = await res.arrayBuffer();
  const type = res.headers.get('content-type') || 'audio/mpeg';
  return { bytes, name: audioName || 'audio', type };
}

/** Decode the current track into an AudioBuffer via the Web Audio API. */
async function decodeCurrentAudio(): Promise<AudioBuffer> {
  const { bytes } = await getCurrentAudioBytes();
  // decodeAudioData detaches the ArrayBuffer, so hand it a private copy and
  // leave the cached buffer intact for later use (e.g. the BPM call).
  const copy = bytes.slice(0);
  const Ctx: typeof AudioContext =
    window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const ctx = new Ctx();
  try {
    return await ctx.decodeAudioData(copy);
  } finally {
    ctx.close().catch(() => { /* ignore */ });
  }
}

/**
 * Detect the first audible onset, skipping leading silence / white noise.
 * Returns the onset time in seconds, rounded to the millisecond.
 *
 * Algorithm: 10 ms RMS windows over the first channel. A relative threshold
 * (a fraction of the track's peak RMS, floored to ignore very quiet hiss)
 * marks "audible". The offset is the first window that crosses the threshold
 * AND stays loud for a few consecutive windows — so a lone click/pop at the
 * very start can't trigger a false onset. We back off a hair so the attack
 * transient of the first real note isn't clipped.
 */
export async function detectAudioOffset(): Promise<number> {
  const buffer = await decodeCurrentAudio();
  const data = buffer.getChannelData(0); // left / mono channel
  const sr = buffer.sampleRate;

  const win = Math.max(1, Math.floor(sr * 0.010)); // 10 ms windows
  const nWin = Math.floor(data.length / win);
  if (nWin === 0) return 0;

  const rms = new Float32Array(nWin);
  let peak = 0;
  for (let w = 0; w < nWin; w++) {
    const base = w * win;
    let sum = 0;
    for (let i = 0; i < win; i++) {
      const s = data[base + i];
      sum += s * s;
    }
    const r = Math.sqrt(sum / win);
    rms[w] = r;
    if (r > peak) peak = r;
  }
  if (peak <= 0) return 0;

  // 8 % of the loudest window, with an absolute floor (~ -42 dBFS) so a quiet
  // hiss intro doesn't read as "music".
  const threshold = Math.max(peak * 0.08, 0.008);
  const sustain = 3; // windows that must stay loud (≈ 30 ms)

  for (let w = 0; w < nWin; w++) {
    if (rms[w] < threshold) continue;
    let ok = true;
    for (let k = 1; k < sustain && w + k < nWin; k++) {
      if (rms[w + k] < threshold * 0.6) { ok = false; break; }
    }
    if (!ok) continue;
    const t = (w * win) / sr - 0.005; // back off 5 ms to keep the attack
    return Math.max(0, Math.round(t * 1000) / 1000);
  }
  return 0;
}

/**
 * Beat-grid offset, phase-locked to a known BPM (server-side). Unlike
 * detectAudioOffset() — which returns the first audible onset ("where the
 * noise starts") — this aligns a full beat grid at `bpm` to the track's onset
 * energy, so the offset matches the tempo instead of a possibly-late first hit.
 * Returns the offset in seconds.
 */
export async function detectAudioBeatOffset(bpm: number): Promise<number> {
  const { bytes, name, type } = await getCurrentAudioBytes();
  const blob = new Blob([bytes], { type: type || 'audio/mpeg' });

  const formData = new FormData();
  formData.append('audio', blob, name);
  if (bpm > 0) formData.append('bpm', String(bpm));

  const res = await fetch(`${API_BASE}/api/audio/analyze`, {
    method: 'POST',
    body: formData,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => `HTTP ${res.status}`);
    throw new Error(text || `Analysis failed (${res.status})`);
  }
  const analysis = await res.json();
  const off = Number(analysis?.offset_seconds);
  if (!Number.isFinite(off) || off < 0) throw new Error('No offset detected');
  return Math.round(off * 1000) / 1000;
}

export interface BpmCandidate {
  bpm: number;
  /** 0..1 — how strongly this octave recurs in the tempogram. */
  strength: number;
}

export interface BpmDetectResult {
  /** Detected musical BPM (sub-grid precise, octave-folded). */
  bpm: number;
  /** 0..1 octave-ambiguity confidence; null if the backend fell back to the
   *  coarse beat_track path. Low ⇒ likely half/double ambiguity. */
  confidence: number | null;
  /** The detected tempo plus its ½× / 2× alternatives, strongest first. */
  candidates: BpmCandidate[];
}

/** Re-run the backend BPM analysis on the loaded track. Returns the detected
 *  BPM together with a confidence score and ½×/2× candidates so the caller can
 *  cross-check the octave (e.g. against the LLM lookup) or offer alternatives. */
export async function detectAudioBpm(): Promise<BpmDetectResult> {
  const { bytes, name, type } = await getCurrentAudioBytes();
  const blob = new Blob([bytes], { type: type || 'audio/mpeg' });

  const formData = new FormData();
  formData.append('audio', blob, name);

  const res = await fetch(`${API_BASE}/api/audio/analyze`, {
    method: 'POST',
    body: formData,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => `HTTP ${res.status}`);
    throw new Error(text || `Analysis failed (${res.status})`);
  }
  const analysis = await res.json();
  const bpm = Number(analysis?.bpm);
  if (!Number.isFinite(bpm) || bpm <= 0) throw new Error('No BPM detected');

  const confRaw = Number(analysis?.bpm_confidence);
  const confidence = Number.isFinite(confRaw) ? confRaw : null;
  const candidates: BpmCandidate[] = Array.isArray(analysis?.bpm_candidates)
    ? analysis.bpm_candidates
        .map((c: any) => ({ bpm: Number(c?.bpm), strength: Number(c?.strength) }))
        .filter((c: BpmCandidate) => Number.isFinite(c.bpm) && c.bpm > 0)
    : [];

  return { bpm: Math.round(bpm * 100) / 100, confidence, candidates };
}
